import React, { useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, UserPlus } from 'lucide-react';
import { assignmentBlock, toastAssignmentBlock } from '@/lib/friendlyCodes';
import { workersRequired, workersAssigned, understaffedText } from './shiftAlertLine';   // UA-6-F19
import { useAppDialog } from '@/hooks/useAppDialog';
import { StaffSelectWithAvailability } from './StaffAvailabilityHelpers';
import { WorkerMatchingPanel } from './WorkerMatchingPanel';
import { CalendarSharedGroupPanel } from './CalendarSharedGroupPanel';
import { PickupRequestsList, pickupRequestsOf } from './PickupRequestsList';   // client#9

/**
 * UC-A-5 Coverage tab (UI-09): the single shared-delivery / coverage surface (CalendarSharedGroupPanel) plus direct worker
 * assignment for an ungrouped shift. Accidental-conflict workers are not assignable (disabled with the reason).
 */
export function ShiftCoverageTab({ shift, staff, permissions, onUpdate, group, onReloadGroup, conflicts, readOnly, segments = [] }) {
  const { dangerConfirm } = useAppDialog();
  const [assigning, setAssigning] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [approvingPickup, setApprovingPickup] = useState(null);   // staff_id being approved
  const canAssign = (permissions.includes('roster.assign') || permissions.includes('roster.manage')) && !readOnly;
  const groupShiftIds = group?.member_shift_ids || (shift.shared_group_id ? [shift.id] : []);

  const handleAssign = async (staffIdArg) => {
    const staffId = typeof staffIdArg === 'string' ? staffIdArg : selectedStaff;
    if (!staffId) return;
    setAssigning(true);
    try {
      try {
        const checkRes = await api.get(`/availability/check?user_id=${staffId}&date=${shift.date}&start_time=${shift.start_time}&end_time=${shift.end_time}`);
        const avail = checkRes.data?.data?.conflicts || [];
        if (avail.length > 0) {
          const ok = await dangerConfirm({ title: 'Heads-up: availability conflict', description: `This staff member has marked ${shift.date} as ${avail.map((c) => c.label).join(', ')}. Assigning anyway is allowed — please confirm before proceeding.`, confirmLabel: 'Assign anyway', cancelLabel: 'Pick someone else', variant: 'warning', testId: 'avail-conflict-confirm' });
          if (!ok) { setAssigning(false); return; }
        }
      } catch { /* non-blocking */ }
      await api.post(`/roster/shifts/${shift.id}/add-staff`, { staff_id: staffId });
      toast.success('Staff assigned'); setSelectedStaff(''); onUpdate();
    } catch (e) {
      const blk = assignmentBlock(e);
      if (blk) toastAssignmentBlock(toast, blk, { 'data-testid': 'assignment-blocked-toast' });
      else notify.error(e, 'SAVE_FAILED');
    } finally { setAssigning(false); }
  };
  const handleRemoveStaff = async (sid) => {
    try { await api.post(`/roster/shifts/${shift.id}/remove-staff`, { staff_id: sid }); toast.success('Removed'); onUpdate(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };
  const handleApprovePickup = async (staffId) => {
    setApprovingPickup(staffId);
    try { await api.post(`/roster/shifts/${shift.id}/pickup-approve`, { staff_id: staffId }); toast.success('Shift request approved — shift assigned'); onUpdate(); }
    catch (e) { const blk = assignmentBlock(e); if (blk) toastAssignmentBlock(toast, blk); else notify.error(e, 'PICKUP_APPROVE_FAILED'); }
    finally { setApprovingPickup(null); }
  };
  const pickupReqs = pickupRequestsOf(shift);

  const conflictFor = (sid) => (conflicts || []).find((c) => String(c.worker_id) === String(sid));
  const needsWorker = workersAssigned(shift) < workersRequired(shift, segments);   // UA-6-F19: planned ratio (2:1 → 2 workers)
  const under = understaffedText(shift, segments);

  return (
    <div className="space-y-3" data-testid="shift-coverage-tab">
      <CalendarSharedGroupPanel shift={shift} staff={staff} onChanged={onUpdate} group={group} onReloadGroup={onReloadGroup} conflicts={conflicts} readOnly={readOnly} />
      {!group && (
        <Card><CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Workers on this shift</CardTitle></CardHeader>
          <CardContent className="p-3 pt-0 space-y-2">
            {shift.assigned_staff?.length > 0 ? shift.assigned_staff.map((s) => {
              const c = conflictFor(s.id);
              return (
                <div key={s.id} className={`flex items-center justify-between rounded-md p-2 ${c ? 'bg-red-50 border border-red-300' : 'bg-slate-50'}`} data-testid={`coverage-worker-${s.id}`}>
                  <span className="text-sm font-medium">{s.name}{c && <span className="block text-[10px] font-normal text-red-800">{c.reason}</span>}</span>
                  {canAssign && <Button size="sm" variant="ghost" className="text-red-500 h-6 text-xs" onClick={() => handleRemoveStaff(s.id)} data-testid={`coverage-worker-remove-${s.id}`}>Remove</Button>}
                </div>
              );
            }) : <p className="text-xs text-muted-foreground" data-testid="coverage-open">Open coverage — no worker assigned.</p>}
            {under && <p className="text-xs text-amber-700" data-testid="coverage-understaffed">{under} — add the second worker (separate assignment; own attendance and timesheet; eligibility, screening and overlap checks apply).</p>}
            {canAssign && needsWorker && (
              <div className="flex gap-2 pt-1">
                <StaffSelectWithAvailability eligibilityShiftId={shift.id} staff={staff} value={selectedStaff} onValueChange={setSelectedStaff} triggerClass="flex-1 h-8 text-xs" testId="drawer-assign-staff-select"
                  filter={(s) => !shift.assigned_staff?.find((a) => a.id === (s.user_id || s.id))} date={shift.date} startTime={shift.start_time} endTime={shift.end_time} excludeShiftId={shift.id}
                  groupShiftIds={groupShiftIds} blockAccidental />
                <Button size="sm" className="h-8" onClick={() => handleAssign()} disabled={assigning || !selectedStaff} data-testid="drawer-assign-staff-btn"><UserPlus className="w-3.5 h-3.5" /></Button>
              </div>
            )}
            {canAssign && needsWorker && permissions.includes('matching.view') && (
              <div className="pt-1"><WorkerMatchingPanel shift={shift} onAssign={handleAssign} assigning={assigning} /></div>
            )}
          </CardContent>
        </Card>
      )}
      {canAssign && pickupReqs.length > 0 && shift.status === 'OPEN' && (
        <Card data-testid="pickup-approval-card" className="border-amber-300 bg-amber-50/60">
          <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2 text-amber-900"><UserPlus className="w-4 h-4" /> {pickupReqs.length === 1 ? 'Pending shift request' : `${pickupReqs.length} shift requests`}</CardTitle></CardHeader>
          <CardContent className="p-3 pt-0 space-y-2">
            {pickupReqs.length > 1 && <p className="text-xs text-amber-900">Approve one worker — the others are told the shift went to someone else.</p>}
            <PickupRequestsList shift={shift} onApprove={handleApprovePickup} approvingId={approvingPickup} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ShiftCoverageTab;
